import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import { isBetBuilderPickId } from '../../core/utils';
import BetbuilderActions from '../bet-builder/actions';
import { ensureLinearBetslip, ensureLinearMode } from '../linear/helpers';
import BetslipPickActions from '../picks/actions';
import { removeAndReplaceGroup } from '../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../picks/helpers';
import { selectBetBuilderTypeStatePicks } from './selectors';

@Injectable()
export class LinearBetBuilderEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
    ) {}

    // When adding a bet builder pick, we're also updating the betBuilder type state
    handleAddPickBetBuilder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            ensureLinearBetslip(),
            concatLatestFrom(() => this.store.select(selectBetBuilderTypeStatePicks)),
            filter(([action, betBuilderPicks]) => !betBuilderPicks[action.pick.id.toString()] && isBetBuilderPickId(action.pick.id)),
            map(([action]) =>
                BetbuilderActions.addPick({
                    pickId: action.pick.id.toString(),
                    pickState: {},
                }),
            ),
        );
    });

    // Handle adding the new pick state to combo type when replace pick is providing a new pick id
    handleReplacePickBetBuilderTypeState$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            ensureLinearBetslip(),
            concatLatestFrom(() => this.store.select(selectBetBuilderTypeStatePicks)),
            filter(([action, betBuilderPicks]) => !betBuilderPicks[action.newPick.id.toString()] && isBetBuilderPickId(action.newPick.id)),
            map(([action, betBuilderPicks]) =>
                BetbuilderActions.addPick({
                    pickId: action.newPick.id.toString(),
                    pickState: { ...betBuilderPicks[action.pickId.toString()] },
                }),
            ),
        );
    });

    // Handle removing the new pick state to combo type when replace pick is providing a new pick id
    handleReplacePickBetBuilderRemoveEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            ensureLinearBetslip(),
            concatLatestFrom(() => this.store.select(selectBetBuilderTypeStatePicks)),
            filter(
                ([action, betBuilderPicks]) =>
                    betBuilderPicks[action.pickId.id.toString()] &&
                    !action.pickId.isEqual(action.newPick.id) &&
                    isBetBuilderPickId(action.newPick.id),
            ),
            map(([action]) =>
                BetbuilderActions.removePick({
                    pickId: action.pickId.id.toString(),
                }),
            ),
        );
    });

    // when there are not enough legs to keep the group we are replacing it with last remaining leg, we need to clean up the BetBuilderState
    handleGroupRemovalFromBetBuilder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removeAndReplaceGroup),
            ensureLinearMode(this.store),
            map((action) => BetbuilderActions.removePick({ pickId: action.pickId.toString() })),
        );
    });

    handleRemovePickFromTypesState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            ensureLinearMode(this.store),
            filter((action) => isBetBuilderPickId(action.pickId)),
            map((action) => BetbuilderActions.removePick({ pickId: action.pickId.toString() })),
        );
    });
}
